import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Share2, Link2, Users, Trophy, Copy, Check, ArrowLeft, History, MessageCircle, Send, X, Mail, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getFingerprint } from '../utils/fingerprint';

const API = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Locale-aware plural for the "hint" word (used both in banner and share text)
export const pluralHintWord = (n, language, t) => {
  if (language === 'ru') {
    const m10 = n % 10, m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return t('virality.hint_one');
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return t('virality.hint_few');
    return t('virality.hint_many');
  }
  if (language === 'zh') return t('virality.hint_many');
  return n === 1 ? t('virality.hint_one') : t('virality.hint_many');
};

// Build the human-readable reason for a points-log entry. Backend now sends
// `reason_key` + `meta`. Old documents still have a plain `reason` string —
// we fall back to that.
export const localizedReason = (item, t) => {
  if (item.reason_key) {
    const params = item.meta || {};
    return t(`virality.${item.reason_key}`, params);
  }
  return item.reason || '';
};

export default function ViralityPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [data, setData] = useState({ links: [], summary: { created: 0, clicks: 0, registrations: 0, points_earned: 0 } });
  const [creating, setCreating] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [pointsLog, setPointsLog] = useState([]);
  const [share, setShare] = useState(null);
  const [previewCopied, setPreviewCopied] = useState(false);
  // sub-mode of the share modal — when 'email' is active, recipient form replaces channels
  const [emailMode, setEmailMode] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    axios.get(`${API}/api/virality/public-config`).then(r => setConfig(r.data)).catch(() => {});
    refresh();
    fetchPointsLog();
  }, [user, navigate]);

  const refresh = () => {
    axios.get(`${API}/api/referrals/my`, { headers: getAuthHeader() })
      .then(r => setData(r.data))
      .catch(() => {});
  };

  const fetchPointsLog = () => {
    axios.get(`${API}/api/referrals/points-log`, { headers: getAuthHeader() })
      .then(r => setPointsLog(r.data?.items || []))
      .catch(() => {});
  };

  const playerName = (user?.full_name || user?.email?.split('@')[0] || 'Player');
  const hintCost = config?.hint_coordinates_cost || 30;

  const buildUrl = (slug) => `${window.location.origin}/game/ref/${slug}`;
  // Append a channel tag so the click can be attributed to the originating
  // messenger downstream (server reads ?via= in /referrals/{slug}/visit).
  const buildUrlVia = (slug, via) => (via ? `${buildUrl(slug)}?via=${encodeURIComponent(via)}` : buildUrl(slug));

  const buildShareText = (slug, via) => {
    const welcome = config?.new_player_welcome_points || 100;
    const hints = Math.floor(welcome / hintCost);
    return t('virality.shareTextTemplate', {
      name: playerName,
      welcome,
      hints,
      hintWord: pluralHintWord(hints, language, t),
      url: buildUrlVia(slug, via),
    });
  };

  // Opens the share modal WITHOUT creating a backend link yet.
  // The link is created lazily on first real share action (ensureLinkCreated).
  // This avoids polluting the DB with abandoned links.
  const openShareModal = () => {
    if (creating) return;
    setShare({
      slug: null,                          // not yet persisted
      url: null,
      // Preview text uses a placeholder so the user can read the message
      // before committing to send. Real URL gets injected by ensureLinkCreated().
      text: buildShareText('XXXXXXXX'),
      subject: t('virality.emailSubject'),
      used: false,
    });
  };

  // Ensure a backend referral link exists. Creates it on first call, then
  // returns the share-state snapshot with real slug/url/text. Subsequent calls
  // return the cached share immediately.
  const ensureLinkCreated = async () => {
    if (share?.slug) return share;
    setCreating(true);
    try {
      let fp = null;
      try { fp = await getFingerprint(); } catch (e) { /* best-effort */ }
      const r = await axios.post(`${API}/api/referrals`, fp ? { device_fingerprint: fp } : {}, { headers: getAuthHeader() });
      const slug = r.data.id;
      const realUrl = buildUrl(slug);
      const realText = buildShareText(slug);
      const updated = {
        slug,
        url: realUrl,
        text: realText,
        subject: share?.subject || t('virality.emailSubject'),
        used: false,
      };
      setShare(updated);
      return updated;
    } catch (e) {
      toast.error(t('virality.toastCreateFail'));
      throw e;
    } finally {
      setCreating(false);
    }
  };

  const markShared = async (slug, channel) => {
    const body = channel ? { channel } : {};
    await axios.post(`${API}/api/referrals/${slug}/mark-shared`, body, { headers: getAuthHeader() }).catch(() => {});
    refresh();
    fetchPointsLog();
  };

  const sendByEmailBackend = async () => {
    if (emailSending) return;
    const rcpt = (emailRecipient || '').trim();
    if (!rcpt || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rcpt)) {
      toast.error(t('virality.emailFormInvalid'));
      return;
    }
    setEmailSending(true);
    let s;
    try {
      s = await ensureLinkCreated();
    } catch (_) {
      setEmailSending(false);
      return;
    }
    try {
      await axios.post(
        `${API}/api/referrals/${s.slug}/send-email`,
        { recipient_email: rcpt },
        { headers: getAuthHeader() },
      );
      toast.success(t('virality.emailFormSent'));
      // mark used so closeShare doesn't delete the link
      setShare(x => x ? { ...x, used: true } : x);
      // close modal
      setEmailMode(false);
      setEmailRecipient('');
      setShare(null);
      refresh();
      fetchPointsLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('virality.emailFormError'));
    } finally {
      setEmailSending(false);
    }
  };

  const sharePickWhatsApp = async () => {
    try {
      const s = await ensureLinkCreated();
      const text = buildShareText(s.slug, 'wa');
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
      setShare(x => x ? { ...x, used: true } : x);
      markShared(s.slug, 'wa');
    } catch (_) {}
  };

  const sharePickMax = async () => {
    // MAX messenger deep link (official): https://max.ru/:share?text=<text>
    try {
      const s = await ensureLinkCreated();
      const text = buildShareText(s.slug, 'max');
      const href = `https://max.ru/:share?text=${encodeURIComponent(text)}`;
      try { await navigator.clipboard.writeText(text); } catch (_) {}
      markShared(s.slug, 'max');
      setShare(null);
      // Top-level navigation: reliable universal-link trigger on iOS/Android.
      window.location.href = href;
    } catch (_) {}
  };

  const sharePickTelegram = async () => {
    try {
      const s = await ensureLinkCreated();
      const url = buildUrlVia(s.slug, 'tg');
      const text = buildShareText(s.slug, 'tg');
      const href = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
      window.open(href, '_blank', 'noopener');
      setShare(x => x ? { ...x, used: true } : x);
      markShared(s.slug, 'tg');
    } catch (_) {}
  };

  const sharePickVk = async () => {
    // VK share endpoint: https://vk.com/share.php?url=...&title=...&description=...
    try {
      const s = await ensureLinkCreated();
      const url = buildUrlVia(s.slug, 'vk');
      const text = buildShareText(s.slug, 'vk');
      const params = new URLSearchParams({
        url,
        title: s.subject || '',
        description: text,
        noparse: 'true',
      });
      const href = `https://vk.com/share.php?${params.toString()}`;
      window.open(href, '_blank', 'noopener,noreferrer,width=700,height=620');
      setShare(x => x ? { ...x, used: true } : x);
      markShared(s.slug, 'vk');
    } catch (_) {}
  };

  const sharePickNative = async () => {
    if (!navigator.share) return;
    let s;
    try {
      s = await ensureLinkCreated();
    } catch (_) { return; }
    try {
      const url = buildUrlVia(s.slug, 'native');
      const text = buildShareText(s.slug, 'native');
      await navigator.share({ title: s.subject, text, url });
      setShare(x => x ? { ...x, used: true } : x);
      markShared(s.slug, 'native');
    } catch (e) { /* user cancelled */ }
  };

  const sharePickCopy = async () => {
    let s;
    try {
      s = await ensureLinkCreated();
    } catch (_) { return; }
    try {
      const text = buildShareText(s.slug, 'copy');
      await navigator.clipboard.writeText(text);
      toast.success(t('virality.toastCopied'));
      setShare(x => x ? { ...x, used: true } : x);
      markShared(s.slug, 'copy');
    } catch {
      toast.info(t('virality.toastCopyManual', { url: buildUrlVia(s.slug, 'copy') }));
    }
  };

  const previewCopy = async () => {
    // Copy the message text. If link not yet created, lazily create one so
    // the text contains a real URL rather than the placeholder slug.
    let s;
    try {
      s = await ensureLinkCreated();
    } catch (_) { return; }
    try {
      const text = buildShareText(s.slug, 'copy');
      await navigator.clipboard.writeText(text);
      setPreviewCopied(true);
      setTimeout(() => setPreviewCopied(false), 1500);
      toast.success(t('virality.toastCopied'));
      setShare(x => x ? { ...x, used: true } : x);
      markShared(s.slug, 'copy');
    } catch {
      toast.info(t('virality.toastCopyManual', { url: buildUrlVia(s.slug, 'copy') }));
    }
  };

  const closeShare = async () => {
    const cur = share;
    setShare(null);
    setEmailMode(false);
    setEmailRecipient('');
    // If a link was actually created (cur.slug set) but never marked used,
    // delete it to keep the DB clean.
    if (cur && cur.slug && !cur.used) {
      await axios.delete(`${API}/api/referrals/${cur.slug}`, { headers: getAuthHeader() }).catch(() => {});
      refresh();
    }
  };

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(buildUrl(slug));
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 1500);
    } catch {
      toast.error(t('virality.toastCopyFail'));
    }
  };

  return (
    <div data-testid="virality-page" style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7ff 0%, #e8efff 40%, #d6e2ff 100%)',
      color: '#1f2937',
      paddingBottom: '3rem',
    }}>
      <style>{`
        .vir-hero { padding: 2.5rem 1.25rem 1.5rem; max-width: 800px; margin: 0 auto; }
        .vir-back-btn { display: inline-flex; align-items: center; gap: 6px; background: none; border: none;
                        color: #1e40af; font-size: 14px; padding: 8px 0; cursor: pointer; font-weight: 500; }
        .vir-back-btn:hover { color: #0a4797; }
        .vir-h1 { font-size: clamp(24px, 5vw, 34px); font-weight: 700; margin: 1rem 0 0.5rem; line-height: 1.15; color: #0f172a; letter-spacing: -0.01em; }
        .vir-lead { font-size: clamp(14px, 2.5vw, 16px); color: #475569; line-height: 1.55; }
        .vir-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 1.5rem 0; }
        .vir-stat { background: white; padding: 16px 12px; border-radius: 12px; text-align: center;
                    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06); border: 1px solid rgba(30, 64, 175, 0.08); }
        .vir-stat-num { font-size: clamp(20px, 4vw, 28px); font-weight: 700; color: #0a4797; line-height: 1; }
        .vir-stat-label { font-size: clamp(11px, 2vw, 12px); color: #64748b; margin-top: 6px; }

        /* === Hero "Invite a friend" CTA === */
        .vir-share-btn {
          --c1: #1e3a8a;
          --c2: #0a4797;
          --c3: #1e5fb8;
          --c4: #3b82f6;
          position: relative; overflow: hidden;
          display: flex; align-items: center; justify-content: center; gap: 14px;
          width: 100%;
          padding: clamp(18px, 3.5vw, 26px) clamp(20px, 4vw, 36px);
          margin-top: 1.5rem;
          background: linear-gradient(135deg, var(--c1) 0%, var(--c2) 35%, var(--c3) 70%, var(--c4) 100%);
          background-size: 200% 200%;
          background-position: 0% 50%;
          border: none; border-radius: 20px;
          color: white;
          font-size: clamp(18px, 3.5vw, 24px); font-weight: 700; letter-spacing: 0.01em;
          cursor: pointer;
          box-shadow:
            0 8px 24px rgba(10, 71, 151, 0.32),
            0 2px 6px rgba(10, 71, 151, 0.18),
            inset 0 1px 0 rgba(255, 255, 255, 0.18);
          transition: transform 0.2s ease, box-shadow 0.2s ease, background-position 0.6s ease;
        }
        .vir-share-btn:hover {
          transform: translateY(-2px) scale(1.005);
          background-position: 100% 50%;
          box-shadow:
            0 14px 34px rgba(10, 71, 151, 0.40),
            0 4px 12px rgba(10, 71, 151, 0.22),
            inset 0 1px 0 rgba(255, 255, 255, 0.22);
        }
        .vir-share-btn:active { transform: translateY(0) scale(0.995); }
        .vir-share-btn:disabled { opacity: 0.65; cursor: wait; transform: none; }
        .vir-share-btn::before {
          /* sheen sweep on hover */
          content: ''; position: absolute; top: 0; left: -120%;
          width: 60%; height: 100%;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%);
          transform: skewX(-18deg);
          transition: left 0.7s ease;
          pointer-events: none;
        }
        .vir-share-btn:hover::before { left: 130%; }
        .vir-share-btn .vir-share-ic {
          display: inline-flex; align-items: center; justify-content: center;
          width: clamp(36px, 6vw, 44px); height: clamp(36px, 6vw, 44px);
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.18);
          border: 1px solid rgba(255, 255, 255, 0.25);
          flex-shrink: 0;
        }

        .vir-points-summary { font-size: 13px; color: #475569; margin-top: 0.9rem; line-height: 1.5; text-align: center; }
        .vir-points-summary b { color: #0f172a; }
        .vir-list { max-width: 800px; margin: 2rem auto 0; padding: 0 1.25rem; }
        .vir-list-h { font-size: 18px; font-weight: 600; margin-bottom: 12px; color: #0f172a; }
        .vir-link-card { background: white; padding: 14px; border-radius: 12px; margin-bottom: 10px;
                         display: flex; gap: 12px; align-items: center;
                         box-shadow: 0 1px 6px rgba(15, 23, 42, 0.05); border: 1px solid rgba(30, 64, 175, 0.06);
                         flex-wrap: wrap; }
        .vir-link-info { flex: 1; min-width: 0; }
        .vir-link-url { font-family: monospace; font-size: 13px; color: #1e40af; word-break: break-all; }
        .vir-link-meta { font-size: 12px; color: #64748b; margin-top: 4px; display: flex; gap: 12px; flex-wrap: wrap; }
        .vir-copy-btn { background: #eff6ff; border: 1px solid #dbeafe; color: #0a4797; padding: 8px 12px;
                        border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 13px;
                        font-weight: 500; }
        .vir-copy-btn:hover { background: #dbeafe; }
        .vir-empty { color: #64748b; font-size: 14px; padding: 16px;
                     background: rgba(255,255,255,0.6); border-radius: 10px;
                     border: 1px dashed rgba(30, 64, 175, 0.18); }
        .vir-log-row { display: flex; gap: 12px; align-items: center;
                       background: white; padding: 12px 14px; border-radius: 10px; margin-bottom: 8px;
                       box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04); border: 1px solid rgba(30, 64, 175, 0.05); }
        .vir-log-pts { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); color: #92400e;
                       border: 1px solid #f59e0b; border-radius: 999px; padding: 4px 10px;
                       font-weight: 700; font-size: 13px; flex-shrink: 0; min-width: 56px; text-align: center; }
        .vir-log-body { flex: 1; min-width: 0; }
        .vir-log-reason { font-size: 14px; color: #0f172a; }
        .vir-log-date { font-size: 12px; color: #64748b; margin-top: 2px; }
        .vir-share-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.55);
                              z-index: 10000; display: flex; align-items: center; justify-content: center;
                              padding: 1rem; animation: vir-fade 0.15s ease-out; }
        @keyframes vir-fade { from { opacity: 0; } to { opacity: 1; } }
        .vir-share-modal { background: white; border-radius: 18px; width: 100%; max-width: 440px;
                           padding: 1.25rem; box-shadow: 0 20px 60px rgba(0,0,0,0.35);
                           color: #0f172a; position: relative;
                           max-height: 90vh; overflow-y: auto; }
        .vir-share-h { font-size: 17px; font-weight: 700; margin-bottom: 4px; }
        .vir-share-sub { font-size: 13px; color: #64748b; margin-bottom: 1rem; line-height: 1.4; }
        .vir-share-close { position: absolute; top: 10px; right: 10px; background: transparent;
                           border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 6px; }
        .vir-share-close:hover { background: #f1f5f9; color: #0f172a; }
        .vir-channels { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
        .vir-ch { display: flex; align-items: center; gap: 10px; padding: 12px; border-radius: 12px;
                  border: 1px solid #e2e8f0; background: #f8fafc; cursor: pointer; font-size: 14px;
                  font-weight: 600; color: #0f172a; transition: all 0.15s; text-align: left; }
        .vir-ch:hover { background: white; border-color: #93c5fd; transform: translateY(-1px);
                        box-shadow: 0 4px 12px rgba(30,64,175,0.1); }
        .vir-ch-ic { width: 32px; height: 32px; border-radius: 8px; display: flex;
                     align-items: center; justify-content: center; color: white; flex-shrink: 0; }
        .vir-ch-email .vir-ch-ic { background: #ea4335; }
        .vir-ch-gmail .vir-ch-ic { background: #ea4335; }
        .vir-ch-mailru .vir-ch-ic { background: #005ff9; color: white; font-weight: 700; }
        .vir-ch-yandex .vir-ch-ic { background: #ffcc00; color: #fc3f1d; font-weight: 700; }
        .vir-ch-ic-max { background: transparent !important; padding: 0 !important; overflow: visible !important; }
        .vir-ch-ic-max svg { display: block; border-radius: 50%; box-shadow: 0 2px 6px rgba(91,63,255,0.35); }
        .vir-ch-vk .vir-ch-ic-vk { background: #0077ff !important; }

        .vir-ch-outlook .vir-ch-ic { background: #0078d4; }
        .vir-ch-mailto .vir-ch-ic { background: #64748b; }
        .vir-ch-wa .vir-ch-ic { background: #25d366; }
        .vir-ch-tg .vir-ch-ic { background: #0088cc; }
        .vir-ch-copy .vir-ch-ic { background: #64748b; }
        .vir-ch-native .vir-ch-ic { background: #0a4797; }
        .vir-ch-server-email .vir-ch-ic { background: #0a4797; }
        .vir-group-h { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase;
                       letter-spacing: 0.05em; margin: 14px 0 8px; }
        .vir-group-h:first-of-type { margin-top: 8px; }
        .vir-via-chips {
          display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
          margin-top: 6px; font-size: 11.5px; color: #475569;
        }
        .vir-via-label { color: #94a3b8; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; margin-right: 2px; }
        .vir-via-chip {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 2px 8px; border-radius: 999px; background: #eef2ff; color: #312e81;
          font-weight: 600; border: 1px solid #c7d2fe;
        }
        .vir-via-chip b { font-weight: 700; }
        .vir-via-wa { background: #dcfce7; color: #065f46; border-color: #86efac; }
        .vir-via-tg { background: #dbeafe; color: #1e3a8a; border-color: #93c5fd; }
        .vir-via-vk { background: #dbeafe; color: #0c4a6e; border-color: #7dd3fc; }
        .vir-via-max { background: #f3e8ff; color: #581c87; border-color: #d8b4fe; }
        .vir-via-email { background: #fef3c7; color: #78350f; border-color: #fcd34d; }
        .vir-via-copy { background: #f1f5f9; color: #334155; border-color: #cbd5e1; }
        .vir-via-native { background: #fae8ff; color: #701a75; border-color: #f0abfc; }
        .vir-share-preview { margin-top: 12px; }
        .vir-preview-ta {
          width: 100%; box-sizing: border-box; min-height: 110px;
          padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 12px;
          font-size: 13.5px; line-height: 1.45; color: #1f2937; resize: vertical;
          background: #f8fafc; outline: none; font-family: inherit;
        }
        .vir-preview-copy-btn {
          margin-top: 8px; display: inline-flex; align-items: center; justify-content: center;
          padding: 9px 16px; border-radius: 10px; border: 1px solid #cbd5e1;
          background: white; color: #0a4797; font-weight: 600; font-size: 13.5px;
          cursor: pointer; transition: all 0.2s ease;
        }
        .vir-preview-copy-btn:hover { background: #f1f5f9; border-color: #94a3b8; }
        .vir-share-link { margin-top: 1rem; padding: 10px 12px; background: #f1f5f9;
                          border-radius: 8px; font-family: monospace; font-size: 12px;
                          color: #1e40af; word-break: break-all; }

        /* === Responsive tweaks === */
        @media (max-width: 640px) {
          .vir-hero { padding: 1.5rem 1rem 1rem; }
          .vir-list { padding: 0 1rem; }
          .vir-stats { gap: 8px; }
          .vir-stat { padding: 12px 8px; border-radius: 10px; }
          .vir-link-card { padding: 12px; gap: 10px; }
          .vir-link-meta { gap: 8px; font-size: 11px; }
          .vir-copy-btn { padding: 6px 10px; font-size: 12px; }
          .vir-channels { grid-template-columns: 1fr; }
          .vir-share-modal { padding: 1rem; border-radius: 14px; }
          .vir-share-btn { gap: 10px; border-radius: 16px; }
        }
        @media (max-width: 380px) {
          .vir-stat-num { font-size: 18px; }
          .vir-stat-label { font-size: 10px; }
          .vir-link-meta span { font-size: 11px; }
        }
      `}</style>

      <div className="vir-hero">
        <button className="vir-back-btn" onClick={() => navigate(-1)} data-testid="virality-back">
          <ArrowLeft size={18} /> {t('common.back')}
        </button>
        <h1 className="vir-h1">{t('virality.pageTitle')}</h1>
        <p className="vir-lead">{t('virality.pageLead')}</p>

        <div className="vir-stats">
          <div className="vir-stat" data-testid="vir-stat-created">
            <div className="vir-stat-num">{data.summary.created}</div>
            <div className="vir-stat-label">{t('virality.statSent')}</div>
          </div>
          <div className="vir-stat" data-testid="vir-stat-clicks">
            <div className="vir-stat-num">{data.summary.clicks}</div>
            <div className="vir-stat-label">{t('virality.statClicks')}</div>
          </div>
          <div className="vir-stat" data-testid="vir-stat-regs">
            <div className="vir-stat-num">{data.summary.registrations}</div>
            <div className="vir-stat-label">{t('virality.statRegs')}</div>
          </div>
        </div>

        <button
          className="vir-share-btn"
          onClick={openShareModal}
          disabled={creating}
          data-testid="vir-create-share"
        >
          <span className="vir-share-ic"><Share2 size={20} /></span>
          {t('virality.createAndShare')}
        </button>
        {config && (
          <div
            className="vir-points-summary"
            dangerouslySetInnerHTML={{
              __html: t('virality.pointsSummary', {
                clickPts: config.link_click_points,
                regPts: config.sender_reg_points,
                total: `<b>${data.summary.points_earned}</b>`,
              }),
            }}
          />
        )}
      </div>

      <div className="vir-list">
        <div className="vir-list-h">{t('virality.myLinksTitle')}</div>
        {data.links.length === 0 && (
          <div className="vir-empty">{t('virality.myLinksEmpty')}</div>
        )}
        {data.links.map(link => (
          <div className="vir-link-card" key={link.id} data-testid={`vir-link-${link.id}`}>
            <Link2 size={18} style={{ color: '#1e40af', flexShrink: 0 }} />
            <div className="vir-link-info">
              <div className="vir-link-url">{buildUrl(link.id)}</div>
              <div className="vir-link-meta">
                <span>{new Date(link.created_at).toLocaleString(language)}</span>
                <span>👀 {link.click_count} {t('virality.abbrClicks')}</span>
                <span><Users size={11} style={{ verticalAlign: 'middle' }} /> {link.registrations_count} {t('virality.abbrRegs')}</span>
                <span><Trophy size={11} style={{ verticalAlign: 'middle' }} /> {link.sender_points_earned} {t('virality.abbrPoints')}</span>
              </div>
              {link.via_clicks && Object.values(link.via_clicks).some(n => n > 0) && (
                <div className="vir-via-chips" data-testid={`vir-via-${link.id}`}>
                  <span className="vir-via-label">{t('virality.viaLabel')}:</span>
                  {Object.entries(link.via_clicks).filter(([,n]) => n > 0).map(([ch, n]) => (
                    <span key={ch} className={`vir-via-chip vir-via-${ch}`}>{t(`virality.via.${ch}`)} <b>{n}</b></span>
                  ))}
                </div>
              )}
            </div>
            <button className="vir-copy-btn" onClick={() => copyLink(link.id)} data-testid={`vir-copy-${link.id}`}>
              {copiedSlug === link.id ? <Check size={14} /> : <Copy size={14} />}
              {copiedSlug === link.id ? t('virality.copied') : t('virality.copy')}
            </button>
          </div>
        ))}
      </div>

      <div className="vir-list" data-testid="vir-points-log">
        <div className="vir-list-h">
          <History size={16} style={{ verticalAlign: 'middle', marginRight: 6, color: '#1e40af' }} />
          {t('virality.historyTitle')}
        </div>
        {pointsLog.length === 0 && (
          <div className="vir-empty">{t('virality.historyEmpty')}</div>
        )}
        {pointsLog.map((it, i) => (
          <div className="vir-log-row" key={i} data-testid={`vir-log-${i}`}>
            <div className="vir-log-pts">+{it.points}</div>
            <div className="vir-log-body">
              <div className="vir-log-reason">{localizedReason(it, t)}</div>
              <div className="vir-log-date">{new Date(it.ts).toLocaleString(language)}</div>
            </div>
          </div>
        ))}
      </div>

      {share && (
        <div
          className="vir-share-backdrop"
          data-testid="vir-share-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeShare(); }}
        >
          <div className="vir-share-modal">
            <button
              className="vir-share-close"
              onClick={closeShare}
              aria-label={t('common.close')}
              data-testid="vir-share-close"
            >
              <X size={18} />
            </button>
            <div className="vir-share-h">{t('virality.shareModalTitle')}</div>
            <div className="vir-share-sub">{t('virality.shareModalSub')}</div>

            {emailMode ? (
              <div data-testid="vir-email-form" style={{ marginTop: 8 }}>
                <div className="vir-group-h">{t('virality.emailFormTitle')}</div>
                <input
                  type="email"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder={t('virality.emailFormPlaceholder')}
                  data-testid="vir-email-input"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '1px solid #cbd5e1',
                    borderRadius: 12,
                    fontSize: 15,
                    outline: 'none',
                    marginBottom: 10,
                    boxSizing: 'border-box',
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendByEmailBackend(); }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => { setEmailMode(false); setEmailRecipient(''); }}
                    style={{
                      flex: '0 0 auto', padding: '12px 18px', borderRadius: 12,
                      border: '1px solid #e2e8f0', background: '#f8fafc',
                      color: '#0f172a', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                    }}
                    data-testid="vir-email-back"
                  >
                    {t('virality.emailFormBack')}
                  </button>
                  <button
                    onClick={sendByEmailBackend}
                    disabled={emailSending}
                    style={{
                      flex: 1, padding: '12px 18px', borderRadius: 12, border: 'none',
                      background: 'linear-gradient(135deg, #0a4797 0%, #1e5fb8 100%)',
                      color: 'white', fontWeight: 700, fontSize: 14, cursor: emailSending ? 'wait' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                    data-testid="vir-email-submit"
                  >
                    {emailSending ? t('virality.emailFormSending') : (<>{t('virality.emailFormSubmit')} <ArrowRight size={16} /></>)}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="vir-group-h">{t('virality.groupEmail')}</div>
                <div className="vir-channels">
                  <button
                    className="vir-ch vir-ch-server-email"
                    onClick={() => setEmailMode(true)}
                    data-testid="vir-ch-server-email"
                    style={{ gridColumn: '1 / -1' }}
                  >
                    <span className="vir-ch-ic"><Mail size={18} /></span>
                    <span>{t('virality.chSendByEmail')}</span>
                  </button>
                </div>

                <div className="vir-group-h">{t('virality.groupMessenger')}</div>
                <div className="vir-channels">
                  <button className="vir-ch vir-ch-wa" onClick={sharePickWhatsApp} data-testid="vir-ch-wa">
                    <span className="vir-ch-ic"><MessageCircle size={18} /></span>
                    <span>{t('virality.chWA')}</span>
                  </button>
                  <button className="vir-ch vir-ch-tg" onClick={sharePickTelegram} data-testid="vir-ch-tg">
                    <span className="vir-ch-ic"><Send size={18} /></span>
                    <span>{t('virality.chTG')}</span>
                  </button>
                  <button className="vir-ch vir-ch-max" onClick={sharePickMax} data-testid="vir-ch-max">
                    <span className="vir-ch-ic vir-ch-ic-max" aria-hidden="true">
                      <svg viewBox="0 0 64 64" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <linearGradient id="maxIcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#5B3FFF"/>
                            <stop offset="100%" stopColor="#FF3D8B"/>
                          </linearGradient>
                        </defs>
                        <circle cx="32" cy="32" r="32" fill="url(#maxIcGrad)"/>
                        <path d="M16 44 L16 20 L23 20 L32 34 L41 20 L48 20 L48 44 L42 44 L42 30 L34 42 L30 42 L22 30 L22 44 Z" fill="#fff"/>
                      </svg>
                    </span>
                    <span>{t('virality.chMax')}</span>
                  </button>
                  <button className="vir-ch vir-ch-vk" onClick={sharePickVk} data-testid="vir-ch-vk">
                    <span className="vir-ch-ic vir-ch-ic-vk" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg" fill="#fff">
                        <path d="M13.162 18.994c.708 0 1.005-.286 1.005-.738.025-1.673.5-2.106 1.302-1.302 1.014.992 1.512 1.748 2.51 2.05.74.224 2.045.092 2.045-.872 0-.526-.504-1.207-1.482-2.18-1.123-1.111-1.025-.94.265-2.578.797-1 1.412-2.27 1.412-2.738 0-.534-.236-.846-1.005-.846l-1.74.012c-.475 0-.81.131-1.005.555-.96 2.117-1.99 3.474-2.524 3.474-.215 0-.293-.13-.293-.846V11.06c0-.524-.144-.836-1-.836h-2.74c-.394 0-.6.196-.6.378 0 .497.738.611.814 2.018v3.04c0 .671-.118.787-.314.787-.527 0-1.736-1.404-2.547-3.61-.13-.354-.448-.628-.879-.628H4.708c-.524 0-.71.275-.71.578 0 .503.59 3.027 3.16 6.345 1.74 2.301 4.024 3.567 6.005 3.586z"/>
                      </svg>
                    </span>
                    <span>{t('virality.chVk')}</span>
                  </button>
                  <button className="vir-ch vir-ch-copy" onClick={sharePickCopy} data-testid="vir-ch-copy">
                    <span className="vir-ch-ic"><Copy size={18} /></span>
                    <span>{t('virality.chCopyText')}</span>
                  </button>
                  {typeof navigator !== 'undefined' && navigator.share && (
                    <button
                      className="vir-ch vir-ch-native"
                      onClick={sharePickNative}
                      data-testid="vir-ch-native"
                    >
                      <span className="vir-ch-ic"><Share2 size={18} /></span>
                      <span>{t('virality.chNative')}</span>
                    </button>
                  )}
                </div>
              </>
            )}
            {!emailMode && (
              <div className="vir-share-preview" data-testid="vir-share-preview-block">
                <div className="vir-group-h" style={{ marginTop: 16 }}>{t('virality.previewTitle')}</div>
                <textarea
                  readOnly
                  data-testid="vir-share-preview-text"
                  value={share.text}
                  className="vir-preview-ta"
                />
                <button
                  className="vir-preview-copy-btn"
                  onClick={previewCopy}
                  data-testid="vir-share-preview-copy"
                >
                  {previewCopied ? <Check size={16} /> : <Copy size={16} />}
                  <span style={{ marginLeft: 6 }}>{previewCopied ? t('virality.copied') : t('virality.previewCopy')}</span>
                </button>
              </div>
            )}
            {share.url && (
              <div className="vir-share-link" data-testid="vir-share-link">{share.url}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
